import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import OvCompactButton from '../atoms/OvCompactButton';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvRecordEditDialog from '../molecules/OvRecordEditDialog';
import OvAddVideoPlacementDialog from './OvAddVideoPlacementDialog';
import OvVideoPlacementRow from './OvVideoPlacementRow';
import { getAxiosErrorMessage } from '../../../common/utils/helpers';
import { useVideoPermissions } from '../../../hooks/use-video-permissions';
import { useVideoPlacements } from '../../../hooks/use-video-placements';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvManageVideoPlacementsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onShowVideos: (key: string) => void;
}

const OvManageVideoPlacementsDialog: FC<OvManageVideoPlacementsDialogProps> = ({
  isOpen,
  onClose,
  onShowVideos,
}) => {
  const { t } = useTranslation();
  const { canCreate } = useVideoPermissions();
  const placements = useVideoPlacements();
  const [isAdding, setIsAdding] = useState(false);

  const renderBody = () => {
    if (placements.isPending) {
      return <OvLoadingIndicator />;
    }

    if (placements.isError) {
      return (
        <OvErrorMessage message={getAxiosErrorMessage(placements.error)} />
      );
    }

    if (!placements.data.length) {
      return <Hint>{t('videos.placements.manage.empty')}</Hint>;
    }

    return (
      <List aria-label={t('videos.placements.manage.listLabel')}>
        {placements.data.map((placement) => (
          <OvVideoPlacementRow
            key={placement.key}
            placement={placement}
            onShowVideos={onShowVideos}
          />
        ))}
      </List>
    );
  };

  return (
    <>
      <OvRecordEditDialog
        isOpen={isOpen}
        title={t('videos.placements.manage.title')}
        onClose={onClose}
      >
        <Header>
          <Hint>{t('videos.placements.manage.hint')}</Hint>
          {canCreate && (
            <OvCompactButton
              icon={<AddIcon />}
              disabled={!placements.isSuccess}
              onClick={() => setIsAdding(true)}
            >
              {t('videos.placements.add.submit')}
            </OvCompactButton>
          )}
        </Header>
        {renderBody()}
      </OvRecordEditDialog>
      {canCreate && (
        <OvAddVideoPlacementDialog
          isOpen={isAdding}
          placements={placements.data ?? []}
          onClose={() => setIsAdding(false)}
        />
      )}
    </>
  );
};

export default OvManageVideoPlacementsDialog;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const Hint = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;
